import React, { useEffect, useState } from 'react';

import { Botao, Field, Modal, Select } from '../../../ui';
import {
  SituacaoEnvioFiltro,
  TipoRegistroEnvio,
} from '../../../api/associado/associadoApi';
import {
  DivergenciaEntidade,
  DivergenciaStatusFiltro,
} from '../../../api/sga-divergencia/sgaDivergenciaApi';

export interface FiltrosEnvios {
  situacao: SituacaoEnvioFiltro;
  /** '' = todos os tipos. Só vale na aba de associados/veículos. */
  tipo: TipoRegistroEnvio | '';
  /** Só valem na aba de divergências (at-32). */
  statusDivergencia: DivergenciaStatusFiltro;
  entidadeDivergencia: DivergenciaEntidade | '';
}

export interface ModalFiltrosEnviosProps {
  aberto: boolean;
  /** Filtros hoje aplicados na tela — viram o rascunho inicial do modal. */
  valor: FiltrosEnvios;
  /** O filtro de tipo é do veículo: não existe na aba de vistorias. */
  mostrarTipo: boolean;
  /** Situação do ENVIO: não existe na aba de divergências. Default true. */
  mostrarSituacao?: boolean;
  /** Status/entidade da divergência: só na aba de divergências (at-32). */
  mostrarDivergencia?: boolean;
  onAplicar: (filtros: FiltrosEnvios) => void;
  onFechar: () => void;
}

export const FILTROS_VAZIOS: FiltrosEnvios = {
  situacao: 'TODOS',
  tipo: '',
  statusDivergencia: 'TODOS',
  entidadeDivergencia: '',
};

/**
 * Conta TODAS as dimensões. Quem troca de aba zera as que não existem lá — sem
 * isso o contador do botão "Filtros" mentiria sobre o que está aplicado
 * (precedente da at-28, quando o filtro de tipo sobrevivia na aba de vistorias).
 */
export const contarFiltrosAtivos = (f: FiltrosEnvios) =>
  (f.situacao !== 'TODOS' ? 1 : 0) +
  (f.tipo ? 1 : 0) +
  (f.statusDivergencia !== 'TODOS' ? 1 : 0) +
  (f.entidadeDivergencia ? 1 : 0);

const OPCOES_TIPO = [
  { valor: '', rotulo: 'Todos' },
  { valor: 'ADESAO', rotulo: 'Adesão' },
  { valor: 'SUBSTITUICAO_VEICULO', rotulo: 'Substituição de veículo' },
  { valor: 'TROCA_TITULAR', rotulo: 'Troca de titular' },
  { valor: 'SUBSTITUICAO_E_TROCA_TITULAR', rotulo: 'Substituição + troca de titular' },
];

const OPCOES_STATUS_DIVERGENCIA = [
  { valor: 'TODOS', rotulo: 'Todos' },
  { valor: 'ABERTA', rotulo: 'Abertas (a resolver)' },
  { valor: 'ACEITA', rotulo: 'Aceitas' },
  { valor: 'IGNORADA', rotulo: 'Ignoradas' },
];

const OPCOES_ENTIDADE = [
  { valor: '', rotulo: 'Todas' },
  { valor: 'ASSOCIADO', rotulo: 'Associado' },
  { valor: 'VEICULO', rotulo: 'Veículo' },
];

/**
 * Filtros do acompanhamento de envios (at-28; reescrito na at-70, frente D/D8).
 *
 * Ficam em modal, e não soltos no cabeçalho: os selects lado a lado com a busca e
 * os contadores não cabiam na barra e quebravam em linhas desalinhadas. Na tela
 * sobra um único botão "Filtros", com a contagem do que está ativo.
 *
 * O modal trabalha sobre um **rascunho** e só devolve no "Aplicar" — assim mexer
 * nos selects não dispara uma requisição por alteração.
 *
 * ## O que mudou na at-70
 *
 *   1. **Virou o `Modal` do kit.** O overlay era o `.filtros-overlay` do CSS
 *      próprio, com listener de `Escape` escrito à mão e `aria-modal="true"` **sem
 *      prender o foco** — a promessa falsa ao leitor de tela que o `Modal` existe
 *      para encerrar. Ganhou foco que entra e volta para o botão "Filtros", Tab
 *      circulando dentro e o título como `<h2>`.
 *   2. **Os campos viraram `Field` + `Select`**, com as `filtros-ajuda` na `dica`.
 *   3. **Rodapé à DIREITA** (`alinharRodape`), a convenção de par de ações.
 *   4. `placeholder=""` nos selects: "Todos"/"Todas" **já é** a opção de "sem
 *      filtro" — um "Selecione..." vazio seria selecionável e gravaria `''` onde a
 *      API espera `'TODOS'`, fazendo o contador mentir (lição da at-65).
 *
 * ⚠️ **O contrato exportado não mudou**: `FiltrosEnvios`, `FILTROS_VAZIOS`,
 * `contarFiltrosAtivos` e as props seguem daqui — a tela os consome.
 *
 * ⚠️ **Os rótulos de situação continuam mudando com `mostrarTipo`**: na aba de
 * associados "pendente" é o titular ou um veículo dele; na de vistorias é o laudo
 * ou as fotos. É a mesma pergunta com dois significados, e o texto tem de dizer
 * qual deles está em jogo.
 */
const ModalFiltrosEnvios: React.FC<ModalFiltrosEnviosProps> = ({
  aberto,
  valor,
  mostrarTipo,
  mostrarSituacao = true,
  mostrarDivergencia = false,
  onAplicar,
  onFechar,
}) => {
  const [rascunho, setRascunho] = useState<FiltrosEnvios>(valor);

  // reabrir o modal tem que refletir o que está aplicado, não o rascunho abandonado
  useEffect(() => {
    if (aberto) setRascunho(valor);
  }, [aberto, valor]);

  const ativos = contarFiltrosAtivos(rascunho);

  return (
    <Modal
      aberto={aberto}
      titulo="Filtros"
      descricao="Valem para a lista. Os contadores do topo são da fila inteira e não mudam."
      tamanho="sm"
      aoFechar={onFechar}
      alinharRodape="direita"
      rodape={
        <>
          <Botao
            variante="fantasma"
            disabled={ativos === 0}
            onClick={() => setRascunho(FILTROS_VAZIOS)}
          >
            Limpar
          </Botao>
          <Botao variante="secundario" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao variante="primario" onClick={() => onAplicar(rascunho)}>
            Aplicar
          </Botao>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {mostrarSituacao && (
          <Field
            rotulo="Situação do envio"
            dica={
              mostrarTipo
                ? 'Pendente = o associado ou algum veículo dele ainda não foi ao SGA.'
                : 'Pendente = o laudo ou as fotos ainda não foram ao SGA.'
            }
            campo="situacao"
          >
            <Select
              opcoes={[
                { valor: 'TODOS', rotulo: 'Todos' },
                {
                  valor: 'PENDENTE',
                  rotulo: mostrarTipo
                    ? 'Com pendência de envio'
                    : 'Com laudo ou fotos pendentes',
                },
                {
                  valor: 'ENVIADO',
                  rotulo: mostrarTipo ? 'Totalmente enviados' : 'Laudo e fotos enviados',
                },
              ]}
              placeholder=""
              value={rascunho.situacao}
              onChange={(e) =>
                setRascunho((r) => ({
                  ...r,
                  situacao: e.target.value as SituacaoEnvioFiltro,
                }))
              }
            />
          </Field>
        )}

        {mostrarDivergencia && (
          <>
            <Field
              rotulo="Status da divergência"
              dica="Aberta = diferença que ainda não foi resolvida por ninguém."
              campo="statusDivergencia"
            >
              <Select
                opcoes={OPCOES_STATUS_DIVERGENCIA}
                placeholder=""
                value={rascunho.statusDivergencia}
                onChange={(e) =>
                  setRascunho((r) => ({
                    ...r,
                    statusDivergencia: e.target.value as DivergenciaStatusFiltro,
                  }))
                }
              />
            </Field>

            <Field rotulo="Entidade" campo="entidadeDivergencia">
              <Select
                opcoes={OPCOES_ENTIDADE}
                placeholder=""
                value={rascunho.entidadeDivergencia}
                onChange={(e) =>
                  setRascunho((r) => ({
                    ...r,
                    entidadeDivergencia: e.target.value as DivergenciaEntidade | '',
                  }))
                }
              />
            </Field>
          </>
        )}

        {mostrarTipo && (
          <Field
            rotulo="Tipo de registro"
            dica="Mostra os associados com ao menos um veículo desse tipo."
            campo="tipo"
          >
            <Select
              opcoes={OPCOES_TIPO}
              placeholder=""
              value={rascunho.tipo}
              onChange={(e) =>
                setRascunho((r) => ({
                  ...r,
                  tipo: e.target.value as TipoRegistroEnvio | '',
                }))
              }
            />
          </Field>
        )}
      </div>
    </Modal>
  );
};

export default ModalFiltrosEnvios;
